import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Assignment, AssignmentTypes, AssignmentOperations, SmartAssignment } from 'types/entegral'
import { BranchInfoFragment, Participant, Vehicle } from 'types/global'
import { AssignmentNetworks } from 'types/global'
import { useCallerLocation, useVehicleLocation } from 'hooks/redux/useLocations'
import { useSelector, useDispatch } from 'react-redux'
import { selectTicket } from 'redux/appStore'
import { setEntegralAssignment, setEntegralParticipant, setEntegralCustomDropoffDestination } from 'redux/slices/kongSlice'
import Button from 'components/Button'
import Margin from 'components/Margin'
import { Paragraph } from 'components/primitives'
import { ErrorMessage } from 'components/fields/Error'
import { Props as ResultProps } from './Results'
import { useServiceProxy } from 'hooks/kong'
import { mapEntegralBusinessHours } from 'utils/entegral'
import { GMapsRoutesDestination, GMapsRoutesOrigin, GMapsRoutesPayload, GMapsRoutesResult } from 'types/googleMaps'
import { LocationPayload } from 'types/location'

type Props = {
  title: string
  vehicle: Vehicle
  network: AssignmentNetworks
} & Pick<ResultProps, 'onSetAssignment'>

export const Create: React.FC<Props> = ({ title, network, vehicle, onSetAssignment }) => {
  const { t } = useTranslation()
  const ticket = useSelector(selectTicket)
  const ticketNumber = ticket.ticketNumber
  const pickupBranch = ticket.pickupInfo.branch as BranchInfoFragment
  const { addressLines, formattedCityStateZip, formattedPhone, groupBranchNumber } = pickupBranch
  const dispatch = useDispatch()
  const [failed, setFailed] = React.useState<Boolean>(false)
  const proxy = useServiceProxy()
  const [loading, setLoading] = React.useState<boolean>(false)
  const vehicleLocation = useVehicleLocation()
  const callerLocation = useCallerLocation()
  const disabled = !network || !vehicle || !vehicleLocation

  const filterType = () => {
    if (network === AssignmentNetworks.SERVICE) {
      return 'serviceCenterNetwork:Yes'
    } else if (network === AssignmentNetworks.TIRE) {
      return 'maintenanceNetwork:Yes'
    } else if (network === AssignmentNetworks.BODY) {
      return 'enterpriseNetwork:Yes'
    }
    return 'enterpriseNetwork:Yes'
  }

  const filterValue = filterType()

  const onClick = async () => {
    try {
      setLoading(true)
      dispatch(setEntegralParticipant(null))
      dispatch(setEntegralCustomDropoffDestination(null))
      // UI Requires one of these locations to be present before being able to search for a network
      const location = (vehicleLocation || callerLocation)!

      const { color, make, model, year, vin } = vehicle ?? {}

      const assignment: Assignment = {
        assignmentType: AssignmentTypes.VEHICLE,
        assignmentOperation: AssignmentOperations.NEW,
        assignmentDetails: {
          vehicle: {
            vin,
            color,
            make,
            model,
            year,
            currentLocation: {
              latitude: location.latitude!,
              longitude: location.longitude!,
            },
          },
          additionalInformation: [
            {
              name: 'rentalAgreementNumber',
              value: ticketNumber,
            },
            {
              name: 'groupBranchNumber',
              value: groupBranchNumber,
            },
            {
              name: 'groupBranchAddress',
              value: `${addressLines && addressLines[0]}, ${formattedCityStateZip}`,
            },
            {
              name: 'groupBranchPhone',
              value: formattedPhone,
            },
            {
              name: 'radius',
              value: 200,
            },
            {
              name: 'radius-units',
              value: 'MILES',
            },
            {
              name: 'enableSmartFilters',
              value: 'false',
            },
            {
              name: 'filters',
              value: filterValue,
            },
          ],
          searchAddress: {
            latitude: location.latitude!,
            longitude: location.longitude!,
          },
        },
      }

      const params = { expanded: 'participants' }

      const queryString = new URLSearchParams(params).toString()

      const { data } = await proxy<SmartAssignment>(
        'post',
        `/serviceproxy/entegral/assignments?${queryString}`,
        {},
        { assignment },
      )

      if (!data) throw new Error('Unable to create network assignment')

      const filteredSmartAssignments = {
        ...data,
        participants: data.participants.filter(
          (participant) => participant && participant.entity.address?.country === location.address.countryShortCode,
        ),
      }

      const dataWithBusinessHours = mapEntegralBusinessHours(filteredSmartAssignments)

      const routesPayload = buildRoutesPayload(location, dataWithBusinessHours)

      let routesList
      let sortedRoutesResult
      let dataSortedByTravelTime

      if (isInRoutesState(vehicleLocation?.address.state)) {
        const { data } = await proxy<GMapsRoutesResult[]>(
          'post',
          `/serviceproxy/googleMaps/routes/`,
          {},
          JSON.stringify(routesPayload),
        )

        routesList = data as GMapsRoutesResult[]

        if (!data) throw new Error('Unable to retrieve routes data')

        //sort the routes by travel duration
        if (routesList) {
          sortedRoutesResult = routesList.sort((a: GMapsRoutesResult, b: GMapsRoutesResult) => {
            let aDur = parseFloat(a.staticDuration.replace('s', ''))
            let bDur = parseFloat(b.staticDuration.replace('s', ''))

            return aDur - bDur
          })

          //sort participants by travel duration
          dataSortedByTravelTime = {
            ...dataWithBusinessHours,
            participants: sortParticipantsByRoutesData(dataWithBusinessHours, sortedRoutesResult),
          }
        }
      }

      setLoading(false)
      if (sortedRoutesResult) {
        dispatch(setEntegralAssignment(dataSortedByTravelTime))
      } else {
        dispatch(setEntegralAssignment(dataWithBusinessHours))
      }
    } catch (e) {
      console.error(e)
      setLoading(false)
      setFailed(true)
    }
  }

  return (
    <Margin spacing="md">
      <Button.Primary loading={loading} disabled={disabled} onClick={onClick} style={{ width: '100%' }}>
        {title}
      </Button.Primary>
      {failed && (
        <>
          <Margin spacing="md">
            <Paragraph as={ErrorMessage}>{t('network.createError')}</Paragraph>
          </Margin>
        </>
      )}
    </Margin>
  )
}

function isInRoutesState(state: string | undefined) {
  if (state === undefined) return false

  //TODO Replace with env variable reference

  const stateList = ['MI', 'WI', 'NY', 'CA']

  if (stateList.includes(state)) return true
}

function buildRoutesPayload(location: LocationPayload, assignment: SmartAssignment) {
  //initialize the payload
  let payload: GMapsRoutesPayload = {
    origins: [],
    destinations: [],
    travelMode: 'DRIVE',
    routingPreference: 'TRAFFIC_UNAWARE',
  }

  //build the origin from location
  let origin: GMapsRoutesOrigin = {
    waypoint: {
      location: {
        latLng: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
      },
    },
  }

  //add origin (only ever 1 origin)
  payload.origins.push(origin)

  assignment.participants.forEach((participant) => {
    if (participant === undefined || participant.location === undefined) return
    if (payload.destinations.length > 9) return

    let loc = participant.location!

    //build destination from participant
    let destination: GMapsRoutesDestination = {
      waypoint: {
        location: {
          latLng: {
            latitude: loc.latitude,
            longitude: loc.longitude,
          },
        },
      },
    }

    //add destination
    payload.destinations.push(destination)
  })

  return payload
}

function sortParticipantsByRoutesData(data: SmartAssignment, sortedResult) {
  let oldParticipants: Participant[] = new Array<Participant>()
  oldParticipants.push(...data.participants)
  let newParticipants: Participant[] = new Array<Participant>()

  sortedResult.forEach((route: GMapsRoutesResult) => {
    //remove sorted result from old participants
    oldParticipants.splice(route.destinationIndex, 1)
    newParticipants.push(data.participants[route.destinationIndex])
  })

  if (oldParticipants.length > 0) {
    //if we still have old participants left (more than 10 results) add them back to the end of the new list
    newParticipants.push(...oldParticipants)
  }

  return newParticipants
}

export default Create
